import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, PenLine } from "lucide-react";
import { UploadDropzone } from "@/components/UploadDropzone";
import { CopyEstimateButton } from "@/components/upload/CopyEstimateButton";
import { api } from "@/lib/api";
import { money } from "@/lib/pricing";
import { useAuth } from "@/context/AuthContext";

export const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

function RecentQuotes() {
  const [quotes, setQuotes] = useState(null);
  useEffect(() => { api.get("/quotes").then((r) => setQuotes(r.data.slice(0, 6))).catch(() => setQuotes([])); }, []);
  return (
    <div className="rounded-2xl border border-slate-800 bg-[#F2F2F7]/60 p-6">
      <div className="mb-5 flex items-center justify-between">
        <h2 className="text-base font-bold text-slate-100 md:text-lg">Recent quotes</h2>
        <Link to="/quotes" data-testid="recent-view-all-link" className="text-xs text-amber-400 hover:underline">View all</Link>
      </div>
      {quotes === null ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-12 animate-pulse rounded-lg bg-slate-800/50" />)}</div>
      ) : quotes.length === 0 ? (
        <p data-testid="recent-quotes-empty" className="py-8 text-center text-sm text-slate-500">No quotes yet. Your saved quotes will show up here.</p>
      ) : (
        <ul className="space-y-1.5" data-testid="recent-quotes-list">
          {quotes.map((q) => (
            <li key={q.id}>
              <Link to={`/quotes/${q.id}`} data-testid={`recent-quote-${q.id}`}
                className="group flex items-center justify-between rounded-lg px-3 py-3 transition-colors hover:bg-slate-800/60">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-slate-100">{q.client_name}</p>
                  <p className="truncate text-xs text-slate-500">{q.number} · {q.project_name || "—"} · {fmtDate(q.created_at)}</p>
                </div>
                <span className="flex items-center gap-2 font-mono-num text-sm text-amber-300">
                  {money(q.final_total)} <ArrowUpRight className="h-3.5 w-3.5 opacity-0 transition-opacity group-hover:opacity-100" />
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { company } = useAuth();
  return (
    <div className="grid min-w-0 gap-10 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]" data-testid="dashboard-page">
      <section className="rise min-w-0">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-400/80">{company?.name}</p>
        <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tight text-slate-50 sm:text-5xl">New quote</h1>
        <p className="mt-4 max-w-xl text-sm text-slate-400 md:text-base">Upload a schedule or drawing. Every window and door is read and priced from your settings.</p>
        <div className="mt-10"><UploadDropzone /></div>
        <CopyEstimateButton />
        <Link to="/quotes/new" data-testid="manual-entry-button"
          className="mt-5 inline-flex items-center gap-2 rounded-full border border-slate-700 px-5 py-2.5 text-sm text-slate-300 transition-colors hover:border-slate-500 hover:text-[#171A20]">
          <PenLine className="h-4 w-4" /> Start a blank quote
        </Link>
      </section>
      <aside className="rise rise-2 min-w-0 lg:pt-24"><RecentQuotes /></aside>
    </div>
  );
}
